import React, { useState } from "react";
import { toast } from "sonner";
import { Envelope, X, Warning } from "@phosphor-icons/react";
import { http } from "@/lib/api";

/**
 * Persistent banner shown at the top of the app while `user.email_verified`
 * is false. Click "Resend" to fire a new verification email (rate-limited
 * server-side to 3/hour). Dismissible for the current session only —
 * the banner returns on next reload until the user actually verifies.
 */
export default function EmailVerifyBanner({ user, onRefreshUser }) {
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  if (!user || user.email_verified || dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      const r = await http.post("/auth/resend-verification");
      toast.success(r.data?.message || "Verification email sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resend.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-[#FFCC00] text-black border-b-2 border-black"
      data-testid="email-verify-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
        <Warning size={16} weight="fill" className="flex-shrink-0" />
        <div className="flex-1 min-w-0 text-[12px] mono">
          <span className="font-black uppercase tracking-widest">Confirm your email</span>
          <span className="mx-2">·</span>
          <span>
            We sent a link to <strong>{user.email}</strong>. Optimizer + subscribing are locked
            until you click it.
          </span>
        </div>
        <button
          onClick={resend}
          disabled={busy}
          data-testid="email-verify-resend"
          className="bg-black hover:bg-zinc-900 text-[#FFCC00] font-black uppercase tracking-widest text-[10px] px-3 py-1.5 disabled:opacity-40 flex items-center gap-1.5"
        >
          <Envelope size={12} weight="fill" />
          {busy ? "Sending…" : "Resend"}
        </button>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="text-black hover:opacity-60"
          data-testid="email-verify-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
